/**
 * Validation rules for Free Text responses (FECOA-2056).
 *
 * A Study Builder can attach an optional rule to a Free Text question. It is
 * stored in the question's options next to the existing character limit:
 *
 *   { type: 'text', maxLength: 500, multiline: true,
 *     validation: { format: 'number', allowDecimals: false, min: 0, max: 31 } }
 *
 * Formats: any, number, letters, alphanumeric, digits. See the API's
 * flexcoa_api/utils/free_text_validation.py for the full description. The
 * API and the mobile app run exactly the same rules, checked against one
 * shared set of examples (__tests__/freeTextValidation.cases.json), so a
 * participant is never told an answer is fine that the server then refuses.
 *
 * * The answer is checked with surrounding spaces, tabs and line breaks
 *   removed; it is always saved exactly as typed.
 * * An empty answer is not checked here; the question's Required setting
 *   decides whether it is allowed.
 * * Letters are Unicode letters. Numbers are the digits 0-9. Spaces are the
 *   space, tab and line break characters.
 * * Length is counted in characters as a person sees them (code points).
 */

export const FREE_TEXT_FORMATS = ['any', 'number', 'letters', 'alphanumeric', 'digits'];
const CHARACTER_FORMATS = ['letters', 'alphanumeric', 'digits'];
// Formats where "at least one letter / number" can still fail, so the rule means something.
export const REQUIRED_CHARACTER_FORMATS = ['any', 'alphanumeric'];

export const MAX_DECIMAL_PLACES = 10;
export const MAX_ALLOWED_CHARACTERS = 50;
export const MAX_ERROR_MESSAGE_LENGTH = 300;
// Limits beyond this cannot be written the same way on every platform.
const MAX_ABSOLUTE_LIMIT = 1000000000000;

const SPACE_CHARACTERS = ' \t\n\r';
const NUMBER_PATTERN = /^-?(?:[0-9]+(?:\.[0-9]+)?|\.[0-9]+)$/;
const LETTER_PATTERN = /^\p{L}$/u;
// A number written in a rule by a builder or an import, as the API reads it.
const CONFIG_NUMBER_PATTERN = /^[+-]?(?:[0-9]+\.?[0-9]*|\.[0-9]+)(?:[eE][+-]?[0-9]+)?$/;

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const asBool = (value, fallback) => (typeof value === 'boolean' ? value : fallback);

const asNumber = (value) => {
  if (typeof value === 'boolean' || value === null || value === undefined) return null;
  let number;
  if (typeof value === 'number') {
    number = value;
  } else if (typeof value === 'string' && CONFIG_NUMBER_PATTERN.test(trimSpaces(value).join(''))) {
    number = Number(trimSpaces(value).join(''));
  } else {
    return null;
  }
  return Number.isFinite(number) ? number : null;
};

const asPositiveInt = (value, maximum = null) => {
  const number = asNumber(value);
  if (number === null || !Number.isInteger(number) || number < 1) return null;
  if (maximum !== null && number > maximum) return null;
  return number;
};

const normalizeAllowedCharacters = (value) => {
  if (typeof value !== 'string') return '';
  const seen = [];
  for (const character of Array.from(value)) {
    if (SPACE_CHARACTERS.includes(character) || seen.includes(character)) continue;
    seen.push(character);
  }
  return seen.slice(0, MAX_ALLOWED_CHARACTERS).join('');
};

/**
 * Returns { rule, problems } for a Study Builder's Free Text rule. The rule
 * keeps only the keys its format uses, with types corrected, and is null when
 * nothing would be checked. Problems are contradictions a builder must fix.
 */
export function normalizeFreeTextValidation(raw, maxLength = null) {
  if (!isPlainObject(raw)) return { rule: null, problems: [] };

  const format = FREE_TEXT_FORMATS.includes(raw.format) ? raw.format : 'any';
  const rule = { format };
  const problems = [];

  if (format === 'number') {
    const allowDecimals = asBool(raw.allowDecimals, true);
    rule.allowDecimals = allowDecimals;
    if (allowDecimals) {
      const places = asPositiveInt(raw.maxDecimalPlaces, MAX_DECIMAL_PLACES);
      if (places !== null) rule.maxDecimalPlaces = places;
    }
    const minimum = asNumber(raw.min);
    const maximum = asNumber(raw.max);
    if ([minimum, maximum].some((v) => v !== null && Math.abs(v) > MAX_ABSOLUTE_LIMIT)) {
      problems.push('The minimum and maximum values must be between -1000000000000 and 1000000000000.');
    }
    if (minimum !== null) {
      rule.min = minimum;
      rule.minInclusive = asBool(raw.minInclusive, true);
    }
    if (maximum !== null) {
      rule.max = maximum;
      rule.maxInclusive = asBool(raw.maxInclusive, true);
    }
    if (minimum !== null && maximum !== null) {
      if (minimum > maximum) {
        problems.push('The minimum value must not be greater than the maximum value.');
      } else if (minimum === maximum && !(rule.minInclusive && rule.maxInclusive)) {
        problems.push('No number can meet this minimum and maximum. Widen the range.');
      }
    }
    if (!allowDecimals && minimum !== null && maximum !== null && problems.length === 0) {
      // The smallest and largest whole numbers inside the range.
      const low = rule.minInclusive ? Math.ceil(minimum) : Math.floor(minimum) + 1;
      const high = rule.maxInclusive ? Math.floor(maximum) : Math.ceil(maximum) - 1;
      if (low > high) {
        problems.push('No whole number can meet this minimum and maximum. Widen the range.');
      }
    }
  }

  if (CHARACTER_FORMATS.includes(format)) {
    rule.allowSpaces = asBool(raw.allowSpaces, false);
    const allowed = normalizeAllowedCharacters(raw.allowedCharacters);
    if (allowed) rule.allowedCharacters = allowed;
  }

  if (format !== 'number') {
    const minLength = asPositiveInt(raw.minLength);
    if (minLength !== null) {
      rule.minLength = minLength;
      const limit = asPositiveInt(maxLength);
      if (limit !== null && minLength > limit) {
        problems.push('The minimum length must not be greater than the maximum length.');
      }
    }
  }

  if (REQUIRED_CHARACTER_FORMATS.includes(format)) {
    if (asBool(raw.requireLetter, false)) rule.requireLetter = true;
    if (asBool(raw.requireNumber, false)) rule.requireNumber = true;
  }

  if (typeof raw.errorMessage === 'string') {
    const message = trimSpaces(raw.errorMessage);
    if (message.length > 0) rule.errorMessage = message.slice(0, MAX_ERROR_MESSAGE_LENGTH).join('');
  }

  const checksSomething =
    format !== 'any' || 'minLength' in rule || 'requireLetter' in rule || 'requireNumber' in rule;
  return { rule: checksSomething ? rule : null, problems };
}

/** The rule attached to a Free Text question's options, if it has one. */
export function getFreeTextValidation(options) {
  if (!isPlainObject(options)) return null;
  return normalizeFreeTextValidation(options.validation, options.maxLength).rule;
}

/**
 * The rule for a participant screen element. Participant screens carry the
 * question's live options; builder previews carry them on the item data.
 */
export function getElementFreeTextValidation(element) {
  if (!element) return null;
  const candidates = [element.options, element.data?.options, element.itemData?.options];
  for (const options of candidates) {
    const rule = getFreeTextValidation(options);
    if (rule) return rule;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Default messages
// ---------------------------------------------------------------------------

/** Plain decimal notation, never an exponent, identical on every platform. */
const formatNumber = (value) => {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(10).replace(/0+$/, '').replace(/\.$/, '');
};

const numberMessage = (rule) => {
  let kind;
  if (rule.allowDecimals === false) {
    kind = 'a whole number';
  } else if (rule.maxDecimalPlaces) {
    const places = rule.maxDecimalPlaces;
    kind = `a number with up to ${places} decimal place${places === 1 ? '' : 's'}`;
  } else {
    kind = 'a number';
  }

  const hasMin = 'min' in rule;
  const hasMax = 'max' in rule;
  if (hasMin && hasMax && rule.minInclusive !== false && rule.maxInclusive !== false) {
    return `Please enter ${kind} between ${formatNumber(rule.min)} and ${formatNumber(rule.max)}.`;
  }
  const parts = [];
  if (hasMin) parts.push(`${rule.minInclusive !== false ? 'at least' : 'greater than'} ${formatNumber(rule.min)}`);
  if (hasMax) parts.push(`${rule.maxInclusive !== false ? 'no more than' : 'less than'} ${formatNumber(rule.max)}`);
  if (parts.length === 0) return `Please enter ${kind}.`;
  return `Please enter ${kind} that is ${parts.join(' and ')}.`;
};

const join = (items) =>
  items.length === 1 ? items[0] : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

const charactersMessage = (rule) => {
  let kinds = { letters: ['letters'], alphanumeric: ['letters', 'numbers'], digits: ['numbers'] }[rule.format];
  if (rule.allowSpaces) kinds = [...kinds, 'spaces'];
  if (rule.allowedCharacters) {
    return `Please use only ${join([...kinds, `these characters: ${Array.from(rule.allowedCharacters).join(' ')}`])}`;
  }
  return `Please use ${join(kinds)} only.`;
};

const lengthMessage = (minimum) =>
  `Please enter at least ${minimum} character${minimum === 1 ? '' : 's'}.`;

// ---------------------------------------------------------------------------
// Checking an answer
// ---------------------------------------------------------------------------

const isLetter = (character) => LETTER_PATTERN.test(character);
const isDigit = (character) => character >= '0' && character <= '9';

/** The characters of `text` without surrounding spaces, tabs and line breaks. */
function trimSpaces(text) {
  const characters = Array.from(text);
  let start = 0;
  let end = characters.length;
  while (start < end && SPACE_CHARACTERS.includes(characters[start])) start += 1;
  while (end > start && SPACE_CHARACTERS.includes(characters[end - 1])) end -= 1;
  return characters.slice(start, end);
}

const checkNumber = (text, rule) => {
  if (!NUMBER_PATTERN.test(text)) return false;
  const dot = text.indexOf('.');
  const decimals = dot >= 0 ? text.slice(dot + 1) : '';
  if (decimals && rule.allowDecimals === false) return false;
  if (rule.maxDecimalPlaces && decimals.length > rule.maxDecimalPlaces) return false;
  const value = Number(text);
  if ('min' in rule && (value < rule.min || (value === rule.min && rule.minInclusive === false))) return false;
  if ('max' in rule && (value > rule.max || (value === rule.max && rule.maxInclusive === false))) return false;
  return true;
};

const checkCharacters = (characters, rule) => {
  const allowed = Array.from(rule.allowedCharacters || '');
  for (const character of characters) {
    if (allowed.includes(character)) continue;
    if (SPACE_CHARACTERS.includes(character)) {
      if (rule.allowSpaces) continue;
      return false;
    }
    if ((rule.format === 'letters' || rule.format === 'alphanumeric') && isLetter(character)) continue;
    if ((rule.format === 'alphanumeric' || rule.format === 'digits') && isDigit(character)) continue;
    return false;
  }
  return true;
};

/** Null when the answer meets the rule, otherwise the message to show. */
export function validateFreeTextAnswer(answer, rule) {
  if (!rule || typeof answer !== 'string') return null;
  const characters = trimSpaces(answer);
  if (characters.length === 0) return null;
  const text = characters.join('');
  const custom = rule.errorMessage || null;

  if (rule.format === 'number') {
    return checkNumber(text, rule) ? null : custom || numberMessage(rule);
  }

  if (CHARACTER_FORMATS.includes(rule.format) && !checkCharacters(characters, rule)) {
    return custom || charactersMessage(rule);
  }

  if (rule.minLength && characters.length < rule.minLength) {
    return custom || lengthMessage(rule.minLength);
  }

  const needsLetter = rule.requireLetter && !characters.some(isLetter);
  const needsNumber = rule.requireNumber && !characters.some(isDigit);
  if (needsLetter && needsNumber) return custom || 'Please include at least one letter and one number.';
  if (needsLetter) return custom || 'Please include at least one letter.';
  if (needsNumber) return custom || 'Please include at least one number.';
  return null;
}

/** The rule in plain words, as the message a participant would see. */
export function describeFreeTextValidation(rule) {
  if (!rule) return null;
  if (rule.errorMessage) return rule.errorMessage;
  if (rule.format === 'number') return numberMessage(rule);
  if (CHARACTER_FORMATS.includes(rule.format)) return charactersMessage(rule);
  if (rule.minLength) return lengthMessage(rule.minLength);
  if (rule.requireLetter && rule.requireNumber) return 'Please include at least one letter and one number.';
  if (rule.requireLetter) return 'Please include at least one letter.';
  if (rule.requireNumber) return 'Please include at least one number.';
  return null;
}

/**
 * The on-screen keyboard that suits the rule, for inputMode. Only answers made
 * of the digits 0-9 get the number pad: a decimal pad shows a comma instead of
 * a point in some regions, and has no minus sign, so anything else keeps the
 * full keyboard.
 */
export function freeTextInputMode(rule) {
  if (!rule) return undefined;
  const digitsOnly =
    (rule.format === 'number' && rule.allowDecimals === false && 'min' in rule && rule.min >= 0) ||
    (rule.format === 'digits' && !rule.allowSpaces && !rule.allowedCharacters);
  return digitsOnly ? 'numeric' : undefined;
}
